import React from 'react';
import { Route, RouteProps } from 'react-router-dom';
import SessionExpiredOrAccessDenied from './SessionExpiredOrAccessDenied';

/**
 * ProtectedRoute — Route avec contrôle d'accès intelligent et fallback unifié.
 *
 * Quand `allowed === true` : rend `component` normalement avec les routeProps.
 * Quand `allowed === false` : rend `<SessionExpiredOrAccessDenied/>` qui :
 *   1. Session expirée (`_user` localStorage absent ou `!isVerified`)
 *      → ouvre SessionTimeoutModal + mémorise pathname pour reconnect + redirect HOME
 *   2. Session valide mais permissions insuffisantes (admin/author requis)
 *      → silent redirect HOME (légitime — pas une expiration)
 *
 * Pourquoi ce composant ? Bug pré-existant :
 *   `<Switch>{cond ? <Route/> : <SessionExpiredOrAccessDenied/>}</Switch>`
 * `<Switch>` n'accepte comme enfants directs que `<Route>` et `<Redirect>`. Tout autre
 * élément React est silencieusement ignoré → écran blanc pour les users non autorisés.
 *
 * ProtectedRoute résout ça en gardant TOUJOURS un `<Route>` dans `<Switch>`, et en
 * déléguant la décision allowed/denied à `render`.
 *
 * Usage :
 *   <ProtectedRoute exact path={EPATHS.COUPON} component={Coupon} allowed={isAuthenticated} />
 *   <ProtectedRoute exact path={EPATHS.SETTINGS} component={Settings} allowed={isAuthenticated && isAdmin} />
 *   <ProtectedRoute exact path={EPATHS.GAIN} component={Gain} allowed={isAuthenticated && (isSnapshot || isAdmin)} />
 *
 * @see src/components/auth/SessionExpiredOrAccessDenied.jsx
 */
interface ProtectedRouteProps extends Omit<RouteProps, 'render' | 'component' | 'children'> {
  /** Composant à rendre quand allowed=true */
  component: React.ComponentType<any>;
  /** Condition d'accès (déjà évaluée par le caller) */
  allowed: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ component: Component, allowed, ...rest }) => (
  <Route
    {...rest}
    render={(routeProps) =>
      allowed ? <Component {...routeProps} /> : <SessionExpiredOrAccessDenied />
    }
  />
);

export default ProtectedRoute;
